import { AppFeaturesKeys } from '@feature-flags';
import { AuthPermission } from '@q6cyber/proto2es/generated/auth';

type SidenavIconClass = 'material-icons-outlined' | 'material-symbols-outlined';

export interface SidenavInterface {
  name: string;
  path?: string;
  icon?: string;
  iconClass?: SidenavIconClass;
  svgIcon?: string;
  featureFlag?: AppFeaturesKeys | AppFeaturesKeys[];
  permissions?: string[];
  children?: SidenavInterface[];
}

export const NAVIGATION_ITEMS: SidenavInterface[] = [
  {
    name: 'Sample Page',
    path: '/sample',
    icon: 'dashboard',
    featureFlag: 'show_sample_page',
  },
  {
    name: 'Organization',
    path: '/user-management',
    icon: 'groups',
    featureFlag: 'show_user_management',
    permissions: [
      'SOME',
      AuthPermission[AuthPermission.TEAM_MANAGER],
      AuthPermission[AuthPermission.TEAM_MANAGER_VIEW],
    ],
  },
  {
    name: 'Client',
    icon: 'business',
    featureFlag: [
      'show_client_subscriptions',
      'show_client_metrics',
      'show_file_share',
    ],
    permissions: [
      'SOME', // This is necessary to check on at least one of the permissions
      AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_VIEW],
      AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_WRITE],
      AuthPermission[AuthPermission.CLIENT_METRICS_VIEW],
      AuthPermission[AuthPermission.FILESHARE_VIEW],
      AuthPermission[AuthPermission.FILESHARE_WRITE],
    ],
    children: [
      {
        name: 'Subscriptions',
        path: '/client/subscriptions',
        icon: 'local_offer',
        featureFlag: 'show_client_subscriptions',
        permissions: [
          'SOME', // This is necessary to check on at least one of the permissions
          AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_VIEW],
          AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_WRITE],
        ],
      },
      {
        name: 'Metrics',
        path: '/client/metrics',
        icon: 'trending_up',
        featureFlag: 'show_client_metrics',
        permissions: [AuthPermission[AuthPermission.CLIENT_METRICS_VIEW]],
      },
      {
        name: 'Subscription List',
        path: '/client/subscription-list',
        icon: 'list',
        featureFlag: 'show_client_subscriptions',
        permissions: [
          'SOME', // This is necessary to check on at least one of the permissions
          AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_VIEW],
          AuthPermission[AuthPermission.CLIENT_SUBSCRIPTIONS_WRITE],
        ],
      },
      {
        name: 'Fileshare',
        path: '/file-share',
        svgIcon: 'fileshare',
        featureFlag: 'show_file_share',
        permissions: [
          'SOME',
          AuthPermission[AuthPermission.FILESHARE_VIEW],
          AuthPermission[AuthPermission.FILESHARE_WRITE],
        ],
      },
    ],
  },
  {
    name: 'Client Portal',
    svgIcon: 'intellihawq',
    featureFlag: [
      'show_banner_manager',
      'show_client_metrics',
      'show_portal_config',
    ],
    permissions: [
      'SOME',
      AuthPermission[AuthPermission.BANNER_MANAGER_VIEW],
      AuthPermission[AuthPermission.BANNER_MANAGER_WRITE],
      AuthPermission[AuthPermission.PORTAL_VIEW],
      AuthPermission[AuthPermission.PORTAL_WRITE],
      AuthPermission[AuthPermission.PORTAL_ADMIN],
    ],
    children: [
      {
        name: 'Banner Manager',
        path: '/banner-manager',
        svgIcon: 'banner_manager',
        featureFlag: 'show_banner_manager',
        permissions: [
          'SOME',
          AuthPermission[AuthPermission.BANNER_MANAGER_VIEW],
          AuthPermission[AuthPermission.BANNER_MANAGER_WRITE],
        ],
      },
      {
        name: 'Portal Config',
        path: '/portal-config',
        svgIcon: 'portal_config',
        featureFlag: 'show_portal_config',
        permissions: [
          'SOME',
          AuthPermission[AuthPermission.PORTAL_VIEW],
          AuthPermission[AuthPermission.PORTAL_WRITE],
          AuthPermission[AuthPermission.PORTAL_ADMIN],
        ],
      },
      {
        name: 'Portal FAQ',
        path: '/portal-faq',
        icon: 'quiz',
        iconClass: 'material-icons-outlined',
        featureFlag: 'show_portal_config',
        permissions: [
          'SOME',
          AuthPermission[AuthPermission.PORTAL_VIEW],
          AuthPermission[AuthPermission.PORTAL_WRITE],
          AuthPermission[AuthPermission.PORTAL_ADMIN],
        ],
      },
    ],
  },
  {
    name: 'Cards',
    icon: 'credit_card',
    permissions: [
      'SOME',
      AuthPermission[AuthPermission.CARDS_FEEDBACK_WRITE],
      AuthPermission[AuthPermission.CARDS_UPLOAD_WRITE],
      AuthPermission[AuthPermission.CARDS_AUTO_PURCHASE_VIEW],
      AuthPermission[AuthPermission.CARDS_AUTO_PURCHASE_WRITE],
    ],
    featureFlag: [
      'show_cards_upload',
      'show_cards_feedback',
      'show_cards_auto_purchase',
    ],
    children: [
      {
        name: 'Cards Upload',
        path: '/cards/new',
        icon: 'add_card',
        permissions: [AuthPermission[AuthPermission.CARDS_UPLOAD_WRITE]],
        featureFlag: 'show_cards_upload',
      },
      {
        name: 'Cards Feedback',
        path: '/cards/feedback',
        icon: 'feedback',
        permissions: [AuthPermission[AuthPermission.CARDS_FEEDBACK_WRITE]],
        featureFlag: 'show_cards_feedback',
      },
      {
        name: 'Auto Purchase',
        path: '/cards/auto-purchase',
        icon: 'shopping_cart',
        permissions: [
          'SOME',
          AuthPermission[AuthPermission.CARDS_AUTO_PURCHASE_WRITE],
          AuthPermission[AuthPermission.CARDS_AUTO_PURCHASE_VIEW],
        ],
        featureFlag: 'show_cards_auto_purchase',
      },
    ],
  },
  {
    name: 'Sources',
    path: '/sources',
    icon: 'mediation',
    featureFlag: 'show_sources',
    permissions: [
      'SOME',
      AuthPermission[AuthPermission.SOURCE_VIEW],
      AuthPermission[AuthPermission.SOURCE_WRITE],
    ],
  },
  {
    name: 'Alert Builder',
    path: '/alert-builder',
    icon: 'campaign',
    featureFlag: 'show_alert_builder',
    permissions: [
      'SOME',
      AuthPermission[AuthPermission.ALERT_BUILDER_VIEW],
      AuthPermission[AuthPermission.ALERT_BUILDER_WRITE],
    ],
  },
  {
    name: 'Threat Entity',
    path: '/threat-entity',
    icon: 'hub',
    featureFlag: 'show_threat_entity',
    permissions: [
      'SOME', // This is necessary to check on at least one of the permissions
      AuthPermission[AuthPermission.THREAT_ENTITY_VIEW],
      AuthPermission[AuthPermission.THREAT_ENTITY_WRITE],
    ],
  },
  {
    name: 'Explore',
    icon: 'category_search',
    iconClass: 'material-symbols-outlined',
    featureFlag: [
      'show_direct_collect_viewer',
      'show_check_record_browser',
      'show_bnr',
      'show_infections',
    ],
    permissions: [
      'SOME',
      AuthPermission[AuthPermission.DIRECT_COLLECT_VIEW],
      AuthPermission[AuthPermission.CHECK_RECORD_VIEW],
      AuthPermission[AuthPermission.BNR_VIEW],
      AuthPermission[AuthPermission.INFECTIONS_VIEW],
    ],
    children: [
      {
        name: 'Direct Collection',
        path: '/direct-collection',
        icon: 'view_timeline',
        featureFlag: 'show_direct_collect_viewer',
        permissions: [AuthPermission[AuthPermission.DIRECT_COLLECT_VIEW]],
      },
      {
        name: 'Check Records',
        path: '/check-records',
        icon: 'receipt_long',
        featureFlag: 'show_check_record_browser',
        permissions: [AuthPermission[AuthPermission.CHECK_RECORD_VIEW]],
      },
      {
        name: 'Botnet Reports',
        path: '/botnet-reports',
        icon: 'smart_toy',
        featureFlag: 'show_bnr',
        permissions: [AuthPermission[AuthPermission.BNR_VIEW]],
      },
      {
        name: 'Infections',
        path: '/infections',
        icon: 'warning_amber',
        featureFlag: 'show_infections',
        permissions: [AuthPermission[AuthPermission.INFECTIONS_VIEW]],
      },
    ],
  },
  {
    name: 'Fraud Alerts',
    path: '/fraud-alerts',
    icon: 'image_search',
    featureFlag: 'show_fraud_alerts',
    permissions: [
      'SOME', // This is necessary to check on at least one of the permissions
      AuthPermission[AuthPermission.FRAUD_ALERTS_VIEW],
      AuthPermission[AuthPermission.FRAUD_ALERTS_WRITE],
      AuthPermission[AuthPermission.FRAUD_ALERTS_ADMIN],
    ],
  },
  {
    name: 'Breach Info',
    path: '/breach-info',
    icon: 'broken_image',
    featureFlag: 'show_breach_info',
    permissions: [
      'SOME',
      AuthPermission[AuthPermission.BREACH_VIEW],
      AuthPermission[AuthPermission.BREACH_WRITE],
    ],
  },
];
